import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { rtctApi } from "services/rtctApi";
import { currentSubscription, pushEnvironment, resend, turnOff, turnOn } from "services/pushClient";
import { badgeText, readNotifications, sameKey, timeAgo } from "utils/notifications";
import { useStableState } from "utils/stableState";

// The top bar's bell (padihat/rtct/notify.py): a node's first report, its return after a long silence and its
// restarts, unread since this person last opened it; and phone notifications for this device. Asked every minute
// while the page is in view; opening it marks everything read (the ones that were unread stay tinted until it closes).
const POLL_MS = 60 * 1000;
const DOT = { new: "bg-emerald-500", back: "bg-blue-500", reset: "bg-amber-500", other: "bg-slate-400" };

export default function NotificationBell() {
  const [state, setState] = useStableState({ data: null, error: "" });
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const [tinted, setTinted] = useState(() => new Set());
  const button = useRef(null);
  const panel = useRef(null);

  const load = useCallback(
    (signal) =>
      Promise.resolve(rtctApi.notifications(signal))
        .then((answer) => {
          if (!signal.aborted) setState({ data: readNotifications(answer), error: "" });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, error: e?.message || "Notifications are unavailable" }));
        }),
    [setState],
  );

  useEffect(() => {
    if (typeof rtctApi.notifications !== "function") return undefined;
    const abort = new AbortController();
    load(abort.signal);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load(abort.signal);
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (!panel.current?.contains(e.target) && !button.current?.contains(e.target)) setOpen(false);
    };
    const key = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const data = state.data;
  const unread = data?.unread || 0;
  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    // Below the bell, kept on screen: a phone's width less 8 px each side, else 24 rem under the bell's right edge.
    const rect = button.current.getBoundingClientRect();
    const width = Math.min(384, window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    setPlace({ top: rect.bottom + 8, left, width });
    setTinted(new Set((data?.notifications || []).filter((n) => n.unread).map((n) => n.id)));
    setOpen(true);
    if (unread > 0) {
      Promise.resolve(rtctApi.notificationsSeen())
        .then(() =>
          setState((old) =>
            old.data
              ? { ...old, data: { ...old.data, unread: 0, notifications: old.data.notifications.map((n) => ({ ...n, unread: false })) } }
              : old,
          ),
        )
        .catch(() => {});
    }
  };

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        title="Notifications"
        className="relative p-2 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-[1.1rem] text-center tabular-nums">
            {badgeText(unread)}
          </span>
        )}
      </button>
      {open && place && (
        <div
          ref={panel}
          role="dialog"
          aria-label="Notifications"
          style={{ position: "fixed", top: place.top, left: place.left, width: place.width }}
          className="z-[60] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-bold text-slate-800">Notifications</h2>
            <p className="text-xs text-slate-500">A node's first report, its return after a long silence, its restarts.</p>
          </div>
          <div className="max-h-[55vh] overflow-y-auto">
            {state.error && !data && <p className="px-4 py-3 text-sm text-red-600">{state.error}</p>}
            {data && data.notifications.length === 0 && (
              <p className="px-4 py-6 text-sm text-slate-500">Nothing yet.</p>
            )}
            {data && data.notifications.length > 0 && (
              <ul>
                {data.notifications.map((n) => (
                  <li key={n.id}>
                    <Link
                      to={n.url || "/node"}
                      onClick={() => setOpen(false)}
                      className={`flex gap-3 px-4 py-3 border-b border-slate-100 ${tinted.has(n.id) ? "bg-blue-50 hover:bg-blue-100/60" : "hover:bg-slate-50"}`}
                    >
                      <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${DOT[n.kind] || DOT.other}`} aria-hidden="true" />
                      <span className="min-w-0">
                        <span className={`block text-sm ${tinted.has(n.id) ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>
                          {n.title}
                        </span>
                        {n.body && <span className="block text-xs text-slate-500 mt-0.5">{n.body}</span>}
                        <span className="block text-[11px] text-slate-400 mt-1">{timeAgo(n.at)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {data && <PhoneNotifications push={data.push} />}
        </div>
      )}
    </div>
  );
}

// Phone notifications for this device: on (with a test), off, or why not (this browser, an iPhone tab that must be
// added to the Home Screen first, blocked by the person, or a server that cannot send them).
function PhoneNotifications({ push }) {
  const [env, setEnv] = useState(() => pushEnvironment());
  const [here, setHere] = useState(undefined);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let live = true;
    if (env !== "ready" || !push.available) {
      setHere(null);
      return undefined;
    }
    Promise.resolve(currentSubscription())
      .then((sub) => {
        if (!live) return;
        // One made with another key (the server's changed) cannot be used: it is replaced when turned on again.
        const usable = sub && sameKey(sub.options && sub.options.applicationServerKey, push.publicKey) ? sub : null;
        setHere(usable);
        if (usable && !push.endpoints.includes(usable.endpoint)) resend(usable).catch(() => {});
      })
      .catch(() => live && setHere(null));
    return () => {
      live = false;
    };
  }, [env, push]);

  const act = async (kind, work) => {
    setBusy(kind);
    setNote("");
    try {
      await work();
    } finally {
      setBusy("");
      setEnv(pushEnvironment());
    }
  };
  const on = () =>
    act("on", async () => {
      try {
        setHere(await turnOn(push.publicKey));
        setNote("On. Send a test to check it arrives.");
      } catch (e) {
        setNote(
          e?.message === "blocked"
            ? "Notifications were not allowed. Allow them for this site in the browser's settings, then try again."
            : `Not turned on: ${e?.message || "the browser refused"}.`,
        );
      }
    });
  const off = () =>
    act("off", async () => {
      try {
        await turnOff();
        setHere(null);
        setNote("Off for this device.");
      } catch (e) {
        setNote(`Not turned off: ${e?.message || "the browser refused"}.`);
      }
    });
  const test = () =>
    act("test", async () => {
      try {
        const results = (await rtctApi.testPush())?.results || [];
        const sent = results.filter((r) => r.ok).length;
        const failed = results.find((r) => !r.ok);
        setNote(
          sent
            ? `Sent to ${sent} device${sent === 1 ? "" : "s"}: it should appear within a few seconds.`
            : `Not sent: ${failed?.error || "no device to send to"}.`,
        );
      } catch (e) {
        setNote(`Not sent: ${e?.message || "the server refused"}.`);
      }
    });

  let content;
  if (!push.available) content = <p>{push.problem || "This server cannot send phone notifications yet."}</p>;
  else if (env === "install")
    content = (
      <p>
        On an iPhone or iPad, add this site to the Home Screen first (Share, then Add to Home Screen), open it from
        there and turn this on.
      </p>
    );
  else if (env === "unsupported") content = <p>This browser cannot show notifications.</p>;
  else if (env === "denied")
    content = <p>Notifications are blocked for this site. Allow them in the browser's site settings, then come back.</p>;
  else if (here === undefined) content = <p>Checking this device…</p>;
  else if (here)
    content = (
      <>
        <p>On for this device.</p>
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={test} disabled={!!busy} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50">
            {busy === "test" ? "Sending…" : "Send a test"}
          </button>
          <button type="button" onClick={off} disabled={!!busy} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            {busy === "off" ? "Turning off…" : "Turn off"}
          </button>
        </div>
      </>
    );
  else
    content = (
      <>
        <p>Get these on this device's screen, even with the dashboard closed.</p>
        <button type="button" onClick={on} disabled={!!busy} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 mt-2">
          {busy === "on" ? "Turning on…" : "Turn on for this device"}
        </button>
      </>
    );

  return (
    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600" aria-label="Phone notifications">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phone notifications</h3>
      {content}
      {note && (
        <p className="mt-2 text-slate-700" role="status">
          {note}
        </p>
      )}
    </div>
  );
}
